import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import './StatusBadge.styles.css';
import type { WaitingStatus } from '../../types/task.types';

export type StatusType =
  | 'en_cours'
  | 'termine'
  | 'a_faire'
  | 'acheve'
  | 'pour_info'
  | 'en_attente_infos'
  | 'a_transmettre'
  | 'en_attente_informations'
  | 'message_laisse';

// Alias for backwards compatibility
export type BadgeType = StatusType;

interface StatusBadgeProps {
  type: StatusType;
  label?: string;
  className?: string;
  interactive?: boolean;
  onStatusChange?: (newStatus: WaitingStatus) => void;
  disabled?: boolean;
}

const STATUS_OPTIONS: { value: WaitingStatus; label: string; type: StatusType }[] = [
  { value: 'a_faire', label: 'A faire', type: 'a_faire' },
  { value: 'acheve', label: 'Achevé', type: 'acheve' },
  { value: 'pour_info', label: 'Pour INFO', type: 'pour_info' },
  { value: null, label: 'En cours', type: 'en_cours' },
  { value: 'en_attente_infos', label: "Attente d'infos", type: 'en_attente_infos' },
  { value: 'a_transmettre', label: 'A transmettre', type: 'a_transmettre' },
  { value: 'en_attente_informations', label: "En attente d'informations", type: 'en_attente_informations' },
  { value: 'message_laisse', label: 'Message laissé', type: 'message_laisse' },
];

const LABELS: Record<StatusType, string> = {
  en_cours: 'En cours',
  termine: 'Terminé',
  a_faire: 'A faire',
  acheve: 'Achevé',
  pour_info: 'Pour INFO',
  en_attente_infos: "Attente d'infos",
  a_transmettre: 'A transmettre',
  en_attente_informations: "En attente d'informations",
  message_laisse: 'Message laissé',
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  type,
  label,
  className = '',
  interactive = false,
  onStatusChange,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Update position when dropdown opens
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 4,
        left: rect.left
      });
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClick = (e: MouseEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Close on scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (value: WaitingStatus) => {
    onStatusChange?.(value);
    setIsOpen(false);
  };

  // Get current status value for comparison
  const getCurrentValue = (): WaitingStatus => {
    const map: Record<string, WaitingStatus> = {
      a_faire: 'a_faire',
      acheve: 'acheve',
      pour_info: 'pour_info',
      en_attente_infos: 'en_attente_infos',
      a_transmettre: 'a_transmettre',
      en_attente_informations: 'en_attente_informations',
      message_laisse: 'message_laisse',
      en_cours: null,
      termine: null,
    };
    return map[type] ?? null;
  };

  const displayLabel = label || LABELS[type] || 'En cours';
  const badgeClass = `status-badge status-${type} ${className}`;

  // Non-interactive badge
  if (!interactive) {
    return <span className={badgeClass}>{displayLabel}</span>;
  }

  // Interactive badge with dropdown
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`${badgeClass} status-interactive`}
        onClick={handleToggle}
        disabled={disabled}
      >
        {displayLabel}
        <ChevronDownIcon className="status-chevron" />
      </button>

      {isOpen && ReactDOM.createPortal(
        <div
          className="status-dropdown"
          style={{ top: coords.top, left: coords.left }}
          onClick={(e) => e.stopPropagation()}
        >
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value || 'en_cours'}
              type="button"
              className={`status-dropdown-item ${getCurrentValue() === opt.value ? 'active' : ''}`}
              onClick={() => handleSelect(opt.value)}
            >
              <span className={`status-dot status-dot-${opt.type}`} />
              {opt.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};
